import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { trackPageView, trackUserInteraction } from '../utils/analytics';
import {Theme} from "../config/theme";

const PrivacyContainer = styled.div`
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
  background: white;
  line-height: 1.6;
  color: ${Theme.colors.main};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
`;

const BackButton = styled.button`
  padding: 0.5rem 1rem;
  margin-right: 1rem;
  background: #f0f0f0;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  
  &:hover {
    background: #e0e0e0;
  }
`;

const Title = styled.h1`
  color: ${Theme.colors.main};
  font-size: 2rem;
`;

const Content = styled.div`
  h2 {
    color: ${Theme.colors.main};
    margin-top: 2rem;
    margin-bottom: 1rem;
    font-size: 1.3rem;
  }

  p {
    margin-bottom: 1rem;
  }

  a {
    color: #0066cc;
    text-decoration: underline;
  }

  ul {
    margin-left: 20px;
    margin-bottom: 1rem;
  }

  li {
    margin-bottom: 0.5rem;
  }
`;

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  // Track page view
  useEffect(() => {
    trackPageView('Privacy Policy', {
      route_path: '/privacy-policy'
    });
  }, []);

  const handleBackClick = () => {
    trackUserInteraction('back_click', {
      from_page: 'privacy_policy'
    });
    
    // Check if there's a previous page in history
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      // If no previous history, go to home
      navigate('/');
    }
  };

  return (
    <PrivacyContainer>
      <Header>
        <BackButton onClick={handleBackClick}>← Back</BackButton>
        <Title>Privacy Policy</Title>
      </Header>
      
      <Content>
        <h2>Privacy Policy for Minecast</h2>
        <p><strong>Effective Date: June 2025</strong></p>
        
        <p>Thank you for choosing to play Minecast. We respect your privacy and are committed to protecting it. This Privacy Policy explains how we handle your information when you use our app.</p>

        <h2>1. Overview</h2>
        <p>Minecast is a screen recorder and color-by-number mobile app that allows users to record their screen and optionally enable the front-facing camera to capture themselves during gameplay. All videos created are stored locally on the user's device. The app does not collect, store, or share any personal information.</p>

        <h2>2. Permissions</h2>
        <p>To enable its features, Minecast may request the following permissions:</p>
        <ul>
          <li><strong>Screen Recording Permission:</strong> To record gameplay.</li>
          <li><strong>Camera Access Permission:</strong> To record the user during gameplay.</li>
          <li><strong>Microphone Access (optional):</strong> If audio narration is used during recording.</li>
        </ul>
        <p>These permissions are used only for these specific purposes. No media or recordings are uploaded, stored, or analyzed by the app.</p>

        <h2>3. Storage of Recordings</h2>
        <ul>
          <li>All recordings (gameplay and camera) are saved locally on the device.</li>
          <li>The app does not upload, analyze, or back up these files.</li>
          <li>Users control whether or not to share their recordings.</li>
        </ul>

        <h2>4. Advertising (AdMob)</h2>
        <p>Minecast displays ads via Google AdMob. These ads help keep the app free and may be personalized based on your device's settings and Google's ad preferences.</p>
        <p>AdMob may collect data such as device type, IP address, advertising ID (e.g., AAID or IDFA), and general usage data.</p>
        <p>You can control ad personalization through your device's privacy settings or Google's ad settings: <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">https://adssettings.google.com</a></p>
        <p>We do not control the data collected by AdMob and recommend reviewing Google's Privacy Policy: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">https://policies.google.com/privacy</a></p>

        <h2>5. Sharing Videos</h2>
        <p>Users may choose to share their recordings using their device's built-in sharing options. The app does not transmit, store, or process these videos externally.</p>

        <h2>6. Analytics and Notifications</h2>
        <p>We use analytics tools such as Google Analytics, Firebase Analytics, and OneSignal to help us understand user engagement, app performance, and to improve the user experience.</p>
        <p>These services may collect non-personal data such as device type, app usage, session length, region, and technical diagnostics. We use this data solely to monitor and improve the app's functionality and performance.</p>
        <p>We also use OneSignal to send push notifications. You may opt out of receiving notifications at any time via your device settings. No personally identifiable information is collected for notification purposes.</p>

        <h2>7. Data Security</h2>
        <p>Because Minecast does not store or transmit personal media or recordings, the security of your recordings remains entirely in your control.</p>

        <h2>8. Changes to this Policy</h2>
        <p>We may update this Privacy Policy to reflect app changes or legal requirements. Any updates will be posted on this page with an updated effective date.</p>

        <h2>9. Contact Us</h2>
        <p>If you have any questions about this Privacy Policy, please contact us:</p>
        <ul>
          <li>Email: support@playcast.me</li>
        </ul>
      </Content>
    </PrivacyContainer>
  );
};

export default PrivacyPolicy; 